/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file type check file - based on sentry
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

/**
 * Checks whether given value's type is one of a few Error or Error-like
 * {@link isError}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isError(wat: any): boolean {
    switch (Object.prototype.toString.call(wat)) {
        case '[object Error]':
            return true;
        case '[object Exception]':
            return true;
        case '[object DOMException]':
            return true;
        default:
            return wat instanceof Error;
    }
}

/**
 * Checks whether given value's type is ErrorEvent
 * {@link isErrorEvent}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isErrorEvent(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object ErrorEvent]';
}

/**
 * Checks whether given value's type is DOMException
 * {@link isDOMException}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isDOMException(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object DOMException]';
}

/**
 * Checks whether given value's type is an undefined
 * {@link isUndefined}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isUndefined(wat: any): boolean {
    return wat === void 0;
}

/**
 * Checks whether given value's type is a function
 * {@link isFunction}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isFunction(wat: any): boolean {
    return typeof wat === 'function';
}

/**
 * Checks whether given value's type is a string
 * {@link isString}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isString(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object String]';
}

/**
 * Checks whether given value's type is a number
 * {@link isNumber}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isNumber(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object Number]';
}

/**
 * Checks whether given value's type is a bool
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isBoolean(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object Boolean]';
}

/**
 * Checks whether given value's is a primitive (undefined, null, number, boolean, string)
 * {@link isPrimitive}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isPrimitive(wat: any): boolean {
    return wat === null || (typeof wat !== 'object' && typeof wat !== 'function');
}

/**
 * Checks whether given value's type is an array
 * {@link isArray}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isArray(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object Array]';
}

/**
 * Checks whether given value's type is an object literal
 * {@link isPlainObject}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isPlainObject(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object Object]';
}

/**
 * Checks whether given value's type is an regexp
 * {@link isRegExp}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isRegExp(wat: any): boolean {
    return Object.prototype.toString.call(wat) === '[object RegExp]';
}

/**
 * Checks whether given value's type is a NaN
 * {@link isNaN}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isNaN(wat: any): boolean {
    return wat !== wat;
}

/**
 * Checks whether given value's type is a SyntheticEvent
 * {@link isSyntheticEvent}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
export function isSyntheticEvent(wat: any): boolean {
    // tslint:disable-next-line:no-unsafe-any
    return (
        isPlainObject(wat) &&
        'nativeEvent' in wat &&
        'preventDefault' in wat &&
        'stopPropagation' in wat
    );
}

/**
 * Checks whether given value is an empty object
 * {@link isEmptyObject}.
 *
 * @param what A value to be checked.
 * @returns A boolean representing the result.
 */
export function isEmptyObject(what: any) {
    if (!isPlainObject(what)) {
        return false;
    }

    for (const item in what) {
        if (what.hasOwnProperty(item)) {
            return false;
        }
    }
    return true;
}

/**
 * Checks whether given value is an object
 * {@link isObject}.
 *
 * @param what A value to be checked.
 * @returns A boolean representing the result.
 */
export function isObject(what: any) {
    return typeof what === 'object' && what !== null;
}
